import { useEffect, useRef, useState } from 'react'
import { deposerPiece, rejeterPiece, retirerPiece, urlSignee, validerPiece, type PieceDossier } from '../../api/onboarding'
import {
  COULEUR_PIECE, kindsProposes, libellePiece, LIBELLE_STATUT_PIECE, messageErreur, piecesRequises, statutPiece,
  type KindPiece, type StatutJuridique,
} from '../../api/onboardingRegles'
import { dateCourte } from '../Format'
import { ListeDeroulante } from '../ListeDeroulante'
import { Bloc, Erreur, Info, useAction } from './Briques'

function Apercu({ chemin }: { chemin: string }) {
  const [url, setUrl] = useState<string | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  useEffect(() => {
    let vivant = true
    urlSignee(chemin).then(
      (u) => vivant && setUrl(u),
      (e: unknown) => vivant && setErreur(messageErreur(e)),
    )
    return () => { vivant = false }
  }, [chemin])
  if (erreur) return <Erreur texte={erreur} />
  if (!url) return <p className="onb-note">Chargement de l’aperçu…</p>
  const ext = (chemin.split('.').pop() ?? '').toLowerCase()
  return (
    <div className="onb-apercu">
      {ext === 'pdf' ? <iframe src={url} title="Aperçu du document" />
        : ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext) ? <img src={url} alt="Aperçu du document" />
        : <p className="onb-note">Ce format ne s’affiche pas ici.</p>}
      <p className="onb-note"><a href={url} target="_blank" rel="noreferrer noopener">Ouvrir dans un nouvel onglet</a> — lien valable 5 minutes.</p>
    </div>
  )
}

function LignePiece({ p, statut, peutDecider, surMaj }: { p: PieceDossier; statut: StatutJuridique | null; peutDecider: boolean; surMaj: () => void }) {
  const [apercu, setApercu] = useState(false)
  const [rejet, setRejet] = useState(false)
  const [motif, setMotif] = useState('')
  const { occupe, erreur, lancer, signaler } = useAction()
  const st = statutPiece(p)
  return (
    <div className="onb-piece">
      <div className="onb-piece-tete">
        <span className="nom">{libellePiece(p.kind, statut)}</span>
        <span className={`badge ${COULEUR_PIECE[st]}`}>{LIBELLE_STATUT_PIECE[st]}</span>
        <div className="onb-piece-actions">
          <button className="btn btn-outline btn-sm" onClick={() => setApercu((a) => !a)}>{apercu ? 'Masquer' : 'Aperçu'}</button>
          {peutDecider && st !== 'valide' && (
            <button className="btn btn-green btn-sm" disabled={occupe} onClick={() => void lancer(async () => { await validerPiece(p.id); surMaj() })}>Valider</button>
          )}
          {peutDecider && st !== 'rejete' && st !== 'valide' && (
            <button className="btn btn-outline btn-sm" disabled={occupe} onClick={() => setRejet((r) => !r)}>Rejeter</button>
          )}
          {peutDecider && st !== 'valide' && (
            <button className="btn btn-ghost btn-sm" disabled={occupe}
              onClick={() => { if (window.confirm('Retirer cette pièce (fichier compris) ?')) void lancer(async () => { await retirerPiece(p); surMaj() }) }}>
              Retirer
            </button>
          )}
        </div>
      </div>
      <div className="onb-piece-detail">
        Déposée le {dateCourte(p.uploaded_at)}
        {p.issued_at && <> · émise le {dateCourte(p.issued_at)}</>}
        {p.rejected_reason && <> · motif du rejet : « {p.rejected_reason} »</>}
      </div>
      {rejet && (
        <div className="onb-actions">
          <input className="grow" placeholder="Motif du rejet (obligatoire)" value={motif} onChange={(e) => setMotif(e.target.value)} />
          <button className="btn btn-sm btn-outline" disabled={occupe}
            onClick={() => {
              if (!motif.trim()) { signaler('Indiquez le motif du rejet de la pièce.'); return }
              void lancer(async () => { await rejeterPiece(p.id, motif); setRejet(false); setMotif(''); surMaj() })
            }}>
            Confirmer le rejet
          </button>
        </div>
      )}
      <Erreur texte={erreur} />
      {apercu && <Apercu chemin={p.file_path} />}
    </div>
  )
}

/**
 * Pièces du dossier. Les pièces EXIGÉES (à VALIDER avant l'envoi du contrat) dépendent du statut juridique :
 * société = RCCM, NIF, identité du représentant ; entreprenant = déclaration d'entreprenant, identité ;
 * personne physique = identité. Fichiers dans le bucket privé `onboarding` (aperçu par lien signé de 5 minutes).
 * Déposer, valider, rejeter, retirer : propriétaire et dispatch ; le comptable lit.
 */
export function SectionPieces({
  orgId, clientId, statut, pieces, peutGerer, bloque, surMaj,
}: {
  orgId: string
  clientId: string
  /** Statut juridique ENREGISTRÉ du client (celui que voit la base) ; null = pas encore choisi. */
  statut: StatutJuridique | null
  pieces: PieceDossier[]
  peutGerer: boolean
  /** Dossier refusé : plus de dépôt tant qu'il n'est pas rouvert. */
  bloque: boolean
  surMaj: () => void
}) {
  const proposes = kindsProposes(statut)
  const [choisi, setKind] = useState<KindPiece>(proposes[0] ?? 'identite_gerant')
  /* Le statut a pu changer (identité enregistrée) : le type choisi doit rester l'un de ceux proposés. */
  const kind: KindPiece = proposes.includes(choisi) ? choisi : (proposes[0] ?? 'identite_gerant')
  const [emisLe, setEmisLe] = useState('')
  const fichier = useRef<HTMLInputElement>(null)
  const { occupe, erreur, info, lancer, signaler } = useAction()

  const manquantes = piecesRequises(statut).filter((k) => !pieces.some((p) => p.kind === k))

  function deposer() {
    const f = fichier.current?.files?.[0]
    if (!f) { signaler('Choisissez un fichier.'); return }
    void lancer(async () => {
      await deposerPiece(orgId, clientId, kind, f, emisLe || undefined)
      if (fichier.current) fichier.current.value = ''
      setEmisLe('')
      surMaj()
      return 'Pièce déposée.'
    })
  }

  return (
    <Bloc titre="Pièces du dossier" droite={`${pieces.filter((p) => p.is_verified).length}/${pieces.length} validée(s)`} lecture={!peutGerer}>
      {!statut && <p className="onb-note">Statut juridique non choisi : seule la pièce d’identité est exigée pour l’instant. Choisissez le statut dans « Identité du client » (et enregistrez) pour connaître toutes les pièces à fournir.</p>}
      {manquantes.map((k) => (
        <div className="onb-piece" key={`m-${k}`}>
          <div className="onb-piece-tete"><span className="nom">{libellePiece(k, statut)}</span><span className="badge b-gray">Non déposée</span></div>
        </div>
      ))}
      {pieces.map((p) => <LignePiece key={p.id} p={p} statut={statut} peutDecider={peutGerer} surMaj={surMaj} />)}
      {pieces.length === 0 && manquantes.length === 0 && <p className="onb-note">Aucune pièce déposée.</p>}
      {peutGerer && !bloque && (
        <div className="onb-depot">
          <div className="field">
            <label>Type de pièce</label>
            <ListeDeroulante valeur={kind} options={proposes.map((k) => ({ valeur: k, libelle: libellePiece(k, statut) }))} surChangement={(v) => setKind(v as KindPiece)} pleineLargeur />
          </div>
          <div className="field">
            <label>Date d’émission (facultatif)</label>
            <input type="date" value={emisLe} onChange={(e) => setEmisLe(e.target.value)} />
          </div>
          <div className="field">
            <label>Fichier (PDF, JPEG, PNG, WebP — 8 Mo max)</label>
            <input ref={fichier} type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic" />
          </div>
          <button className="btn btn-gold btn-sm" disabled={occupe} onClick={deposer}>{occupe ? 'Dépôt…' : 'Déposer'}</button>
        </div>
      )}
      {bloque && <p className="onb-note">Dossier refusé : rouvrez-le pour déposer une pièce.</p>}
      <Erreur texte={erreur} />
      <Info texte={info} />
    </Bloc>
  )
}
